import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API, GOOGLE_CLIENT_ID } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Scissors, Phone, ArrowLeft, Loader2, Mail } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const BRAND_NAME = "Salon Bahadır Kersü";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export default function CustomerAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [authMethod, setAuthMethod] = useState("phone"); // phone, email
  const [step, setStep] = useState("input"); // input, otp, name
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isNewUser, setIsNewUser] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);

  const from = location.state?.from || "/dashboard";

  // Google OAuth callback
  const handleGoogleCallback = useCallback(async (response) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/google`, {
        credential: response.credential
      });
      if (res.data.success) {
        login(res.data.token, res.data.user, "customer");
        toast.success("Google ile giriş başarılı!");
        navigate(from);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Google ile giriş başarısız";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [login, navigate, from]);

  // Initialize Google Sign-In
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCallback,
        });
        window.google.accounts.id.renderButton(
          document.getElementById("google-signin-btn"),
          {
            theme: "outline",
            size: "large",
            width: "100%",
            text: "signin_with",
            shape: "rectangular",
          }
        );
      }
    };
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [handleGoogleCallback]);

  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.startsWith("90")) {
      return "+90 " + numbers.slice(2, 5) + " " + numbers.slice(5, 8) + " " + numbers.slice(8, 10) + " " + numbers.slice(10, 12);
    }
    if (numbers.length <= 10) {
      return "+90 " + numbers.slice(0, 3) + " " + numbers.slice(3, 6) + " " + numbers.slice(6, 8) + " " + numbers.slice(8, 10);
    }
    return value;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  const startCountdown = () => {
    setCountdown(60);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    const cleanPhone = phone.replace(/\s/g, "");

    if (cleanPhone.length < 13) {
      toast.error("Geçerli bir telefon numarası girin");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-otp`, { phone: cleanPhone });
      toast.success("Doğrulama kodu gönderildi");
      setStep("otp");
      startCountdown();
    } catch (error) {
      const message = error.response?.data?.detail || "Kod gönderilemedi";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (otp.length !== 6) {
      toast.error("6 haneli kodu girin");
      return;
    }

    setLoading(true);
    try {
      const cleanPhone = phone.replace(/\s/g, "");
      const response = await axios.post(`${API}/auth/verify-otp`, {
        phone: cleanPhone,
        otp: otp
      });

      if (response.data.success) {
        if (response.data.is_new_user) {
          setIsNewUser(true);
          setPendingToken(response.data.token);
          setPendingUser(response.data.user);
          setStep("name");
          toast.success("Hoş geldiniz! Lütfen adınızı girin.");
        } else {
          login(response.data.token, response.data.user, "customer");
          toast.success("Giriş başarılı!");
          navigate(from);
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Doğrulama başarısız";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSetName = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Lütfen adınızı girin");
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API}/customers/profile?name=${encodeURIComponent(name)}`,
        {},
        { headers: { Authorization: `Bearer ${pendingToken}` } }
      );

      login(pendingToken, { ...pendingUser, name }, "customer");
      toast.success("Hesabınız oluşturuldu!");
      navigate(from);
    } catch (error) {
      const message = error.response?.data?.detail || "İşlem başarısız";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    await handleRequestOTP({ preventDefault: () => {} });
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Scissors className="w-8 h-8 text-[#c9a96e]" />
            <span className="font-display text-2xl font-semibold text-[#1a1a2e]">{BRAND_NAME}</span>
          </div>
          <p className="text-[#6b7280]">
            {step === "input" && "Hesabınıza giriş yapın"}
            {step === "otp" && "Doğrulama kodunu girin"}
            {step === "name" && "Hesabınızı tamamlayın"}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white border border-[#e5e7eb] rounded-xl p-8 shadow-sm">

          {step === "input" && (
            <div className="space-y-6">
              {/* Google Login */}
              <div>
                <div id="google-signin-btn" className="w-full flex justify-center"></div>
              </div>

              <div className="divider-text">veya</div>

              {/* Phone Login */}
              <form onSubmit={handleRequestOTP} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#6b7280] mb-2 block">Telefon Numarası</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9ca3af]" />
                    <Input
                      type="tel"
                      placeholder="+90 5XX XXX XX XX"
                      value={phone}
                      onChange={handlePhoneChange}
                      className="input-elite pl-12 h-12"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="btn-primary w-full h-12"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    "Kod Gönder"
                  )}
                </Button>
              </form>
            </div>
          )}

          {step === "otp" && (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <button
                type="button"
                onClick={() => setStep("input")}
                className="flex items-center gap-2 text-[#6b7280] hover:text-[#1a1a2e] transition-colors mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Geri</span>
              </button>

              <div>
                <label className="text-sm font-medium text-[#6b7280] mb-4 block text-center">
                  {phone} numarasına gönderilen 6 haneli kodu girin
                </label>
                <div className="flex justify-center">
                  <InputOTP
                    value={otp}
                    onChange={setOtp}
                    maxLength={6}
                  >
                    <InputOTPGroup className="gap-2">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <InputOTPSlot
                          key={index}
                          index={index}
                          className="w-12 h-14 text-xl bg-[#fafafa] border-[#e5e7eb] text-[#1a1a2e] rounded-lg"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>

              <Button
                type="submit"
                className="btn-primary w-full h-12"
                disabled={loading || otp.length !== 6}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Doğrula"
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={countdown > 0}
                  className="text-sm text-[#6b7280] hover:text-[#c9a96e] disabled:text-[#d1d5db] disabled:cursor-not-allowed transition-colors"
                >
                  {countdown > 0 ? `${countdown}s sonra tekrar gönder` : "Kodu tekrar gönder"}
                </button>
              </div>
            </form>
          )}

          {step === "name" && (
            <form onSubmit={handleSetName} className="space-y-6">
              <div>
                <label className="text-sm font-medium text-[#6b7280] mb-2 block">Adınız Soyadınız</label>
                <Input
                  type="text"
                  placeholder="Adınızı girin"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-elite h-12"
                />
              </div>

              <Button
                type="submit"
                className="btn-primary w-full h-12"
                disabled={loading || !name.trim()}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Hesabı Oluştur"
                )}
              </Button>
            </form>
          )}
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate("/")}
            className="text-sm text-[#6b7280] hover:text-[#1a1a2e] transition-colors"
          >
            Ana sayfaya dön
          </button>
        </div>
      </div>
    </div>
  );
}
